import { buildPalette, rgba, A4 } from '../theme/PrintTheme';
import type { PrintPalette } from '../theme/PrintTheme';
import { PRINT_TEMPLATE_LIST, resolvePrintTemplate } from '../theme/PrintTemplate';
import type { PrintTemplate as PrintDesign } from '../theme/PrintTemplate';
import { documentBodyHtml, documentFor, documentLabel } from './printDocuments';
import type { PrintTemplate } from '../types';

/**
 * The bridge between a *design* and a *document*.
 *
 * A design (`theme/PrintTemplate.ts`) describes shape only: where the logo
 * sits, whether the masthead is a band or a sidebar, how tables are ruled. A
 * document supplies the content, drawn from its own verified fields. Neither
 * knows about the other, which is why ten designs cover twenty-three documents
 * without a marriage certificate ever seeing a baptism field.
 *
 * What comes out is ordinary template HTML for a print template row — the same
 * three columns the register already has. Choosing a design writes a normal
 * row that can afterwards be edited by hand like any other.
 */

/** The parish emblem, or its name set in type when none has been uploaded. */
const logoBlock = (height: string, plate: boolean): string =>
  `{{#churchLogo}}<img class="logo" src="{{churchLogo}}" alt="{{churchName}}"`
  + ` style="height:${height};${plate ? 'background:#FFF;padding:5px;border-radius:4px;' : ''}">`
  + `{{/churchLogo}}`
  + `{{^churchLogo}}<span class="logo-fallback">{{churchName}}</span>{{/churchLogo}}`;

const identity = (withContact: boolean): string =>
  `<div class="identity">`
  + `<p class="church-name">{{churchName}}</p>`
  + `<p class="church-addr">{{churchAddress}}</p>`
  + (withContact ? `<p class="church-tel">{{churchContactNo}}</p>` : '')
  + `</div>`;

const titleBlock = (label: string): string => `<h1 class="doc-title">${label}</h1>`;

/** Headers that put the logo on a filled area, where dark artwork would vanish. */
const ON_COLOUR = ['band', 'diagonal', 'wave', 'split', 'sidebar'];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const headerHtmlFor = (d: PrintDesign, label: string): string => {
  const plate = ON_COLOUR.includes(d.header);
  const logo = d.logo === 'none' ? '' : logoBlock(d.logoHeight, plate);
  const inHeader = d.title === 'in-header';

  if (d.header === 'sidebar') {
    // The sidebar is drawn by the body's grid; the header carries its content.
    return `<div class="masthead sidebar">${logo}${identity(true)}</div>`;
  }

  if (d.header === 'stacked' || d.header === 'outline') {
    return `<div class="masthead stacked">${logo}${identity(false)}`
      + (d.title === 'ribbon' ? '' : titleBlock(label))
      + `</div>`
      + (d.title === 'ribbon' ? `<div class="ribbon">${titleBlock(label)}</div>` : '');
  }

  // band / diagonal / wave / split / plain all share one arrangement: the
  // parish on one side, and the title on the other when it rides in the header.
  const order = d.logo === 'right' ? 'reverse' : 'normal';
  return `<div class="masthead ${d.header} ${order}">`
    + `<div class="masthead-left">${logo}${identity(!inHeader)}</div>`
    + (inHeader ? titleBlock(label) : '')
    + `</div>`
    + (inHeader ? '' : titleBlock(label));
};

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

const footerHtmlFor = (d: PrintDesign): string => {
  if (d.footer === 'none') return '';
  if (d.footer === 'centered-note') {
    return `<div class="foot note">{{churchName}} &middot; {{churchContactNo}}</div>`;
  }
  return `<div class="foot ${d.footer}">`
    + `<span>{{churchName}} &middot; {{churchContactNo}}</span>`
    + `<span>Printed {{currentDate}} by {{generatedBy}}</span>`
    + `</div>`;
};

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

const headerCss = (d: PrintDesign, p: PrintPalette): string => {
  const band = `background:${p.accent};color:${p.onAccent};`;

  switch (d.header) {
    case 'band':
      return `.masthead{${band}padding:22px 16mm;min-height:${d.bandHeight};`
        + `display:flex;align-items:center;justify-content:space-between;gap:${d.headerGap};}`;
    case 'split':
      return `.masthead{background:linear-gradient(90deg,${p.accent} 0%,${p.accent} 58%,`
        + `${p.accentPale} 58%);color:${p.onAccent};padding:22px 16mm;min-height:${d.bandHeight};`
        + `display:flex;align-items:center;justify-content:space-between;gap:${d.headerGap};}`
        + `.masthead .doc-title{color:${p.accentDark};}`;
    case 'diagonal':
      return `.masthead{${band}padding:22px 16mm 46px;min-height:${d.bandHeight};`
        + `display:flex;align-items:center;justify-content:space-between;gap:${d.headerGap};`
        + `clip-path:polygon(0 0,100% 0,100% 74%,0 100%);position:relative;}`
        + `.masthead::after{content:"";position:absolute;inset:0;background:${p.accentDark};`
        + `clip-path:polygon(58% 0,100% 0,100% 64%);opacity:.5;}`
        + `.masthead > *{position:relative;z-index:1;}`;
    case 'wave':
      return `.masthead{${band}padding:22px 16mm 40px;min-height:${d.bandHeight};`
        + `display:flex;align-items:center;justify-content:space-between;gap:${d.headerGap};`
        + `border-bottom-left-radius:70% 44px;border-bottom-right-radius:70% 44px;}`;
    case 'sidebar':
      return `.masthead{${band}padding:26px 18px;display:flex;flex-direction:column;`
        + `gap:14px;width:${d.bandHeight};}`
        + `.masthead .church-addr,.masthead .church-tel{opacity:.92;}`;
    case 'outline':
      return `.masthead{border:2px solid ${p.accent};padding:18px 22px;text-align:center;`
        + `margin-bottom:${d.headerGap};}`;
    case 'stacked':
      return `.masthead{text-align:center;border-top:${d.bandHeight} solid ${p.accent};`
        + `border-bottom:1px solid ${p.accent};padding:16px 0 13px;margin-bottom:${d.headerGap};}`;
    default:
      return `.masthead{display:flex;align-items:center;gap:${d.headerGap};`
        + `padding-bottom:14px;border-bottom:1px solid ${p.rule};}`
        + `.masthead.reverse{flex-direction:row-reverse;text-align:right;}`;
  }
};

const titleCss = (d: PrintDesign, p: PrintPalette): string => {
  const base = `text-transform:${d.titleTransform};letter-spacing:${d.titleSpacing};`
    + `font-family:${d.headingFont};`;

  switch (d.title) {
    case 'in-header':
      return `.doc-title{${base}font-size:19px;font-weight:700;margin:0;text-align:right;max-width:48%;}`;
    case 'ribbon':
      return `.ribbon{background:${p.accentDark};color:${p.onAccent};margin:0 10mm;`
        + `padding:11px 20px;text-align:center;}`
        + `.ribbon .doc-title{${base}font-size:17px;font-weight:700;margin:0;}`;
    case 'boxed':
      return `.doc-title{${base}font-size:17px;font-weight:700;margin:${d.headerGap} 16mm 16px;`
        + `padding:9px 14px;border-left:4px solid ${p.accent};background:${p.accentPale};`
        + `color:${p.accentDark};}`;
    case 'centered-rule':
      return `.doc-title{${base}font-size:20px;font-weight:400;margin:22px auto 20px;`
        + `text-align:center;color:${p.accentDark};padding-bottom:9px;`
        + `border-bottom:1px solid ${p.accent};display:table;}`;
    default:
      return `.doc-title{${base}font-size:22px;font-weight:700;margin:${d.headerGap} 0 16px;`
        + `color:${p.accentDark};}`;
  }
};

const tableCss = (d: PrintDesign, p: PrintPalette): string => {
  // Amounts are right-aligned by their `num` mark, never by position: aligning
  // the last column put a name such as "Approved By" against the right edge
  // and left Receipt / Debit columns ragged in the middle of the table.
  const cell = `.rows th,.rows td{padding:${d.cellPadding};text-align:left;vertical-align:top;}`
    + `.rows .num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;}`
    + `.rows.wide{font-size:10.5px;}.rows.wide th,.rows.wide td{padding:5px 6px;}`
    + `.rows td{word-break:break-word;}.rows thead{display:table-header-group;}`
    + `.rows tr{break-inside:avoid;page-break-inside:avoid;}`
    // The figures printed under a register (totals) sit to the right, as a
    // ledger's totals do, and their amounts line up with the columns above.
    + `.rows + .ref + .details{margin-top:10px;margin-left:auto;max-width:360px;}`
    + `.rows + .ref + .details td{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;}`;

  switch (d.table) {
    case 'headband':
      return cell + `.rows th{background:${p.accent};color:${p.onAccent};font-weight:700;}`
        + `.rows td{border-bottom:1px solid ${p.ruleSoft};}`
        + `.rows tbody tr:nth-child(even){background:${p.accentPale};}`;
    case 'striped':
      return cell + `.rows th{border-bottom:2px solid ${p.accent};color:${p.accentDark};font-weight:700;}`
        + `.rows tbody tr:nth-child(even){background:${rgba(p.accent, 0.07)};}`
        + `.rows td{border-bottom:1px solid ${p.ruleSoft};}`;
    case 'boxed':
      return cell + `.rows th{background:${p.accentDark};color:${p.onAccent};font-weight:700;}`
        + `.rows th,.rows td{border:1px solid ${p.ruleSoft};}`;
    case 'minimal-rule':
      return cell + `.rows th{border-bottom:1.5px solid ${p.ink};font-weight:700;}`
        + `.rows td{border-bottom:1px solid ${p.ruleSoft};}`;
    case 'plain':
      return cell + `.rows th{font-weight:700;}`;
    default:
      return cell + `.rows th{border-bottom:2px solid ${p.accent};color:${p.accentDark};font-weight:700;}`
        + `.rows td{border-bottom:1px solid ${p.ruleSoft};}`;
  }
};

const footerCss = (d: PrintDesign, p: PrintPalette): string => {
  switch (d.footer) {
    case 'band':
      return `.foot{background:${p.accent};color:${p.onAccent};padding:11px 16mm;`
        + `display:flex;justify-content:space-between;font-size:10.5px;margin-top:24px;}`;
    case 'double-rule':
      return `.foot{border-top:3px double ${p.accent};padding-top:9px;margin-top:24px;`
        + `display:flex;justify-content:space-between;font-size:10.5px;color:${p.inkSoft};}`;
    case 'centered-note':
      return `.foot{margin-top:22px;text-align:center;font-size:10.5px;color:${p.inkFaint};}`;
    default:
      return `.foot{border-top:1px solid ${p.accent};padding-top:9px;margin-top:24px;`
        + `display:flex;justify-content:space-between;font-size:10.5px;color:${p.inkSoft};}`;
  }
};

const decorationCss = (d: PrintDesign, p: PrintPalette): string => {
  switch (d.decoration) {
    case 'corner-triangle':
      return `.sheet::after{content:"";position:absolute;right:0;bottom:0;border-style:solid;`
        + `border-width:0 0 100px 100px;border-color:transparent transparent ${p.accentPale} transparent;}`;
    case 'corner-arcs':
      return `.sheet::after{content:"";position:absolute;right:-60px;top:-60px;width:170px;`
        + `height:170px;border-radius:50%;background:${rgba(p.accent, 0.1)};}`;
    case 'side-accent':
      return `.sheet::before{content:"";position:absolute;left:0;top:0;bottom:0;width:5px;`
        + `background:${p.accent};}`;
    case 'gradient-bar':
      return `.sheet::before{content:"";position:absolute;left:0;right:0;top:0;height:5px;`
        + `background:linear-gradient(90deg,${p.accentDark},${p.accent},${p.accentLight});}`;
    default:
      return '';
  }
};

const borderCss = (d: PrintDesign, p: PrintPalette): string => {
  switch (d.border) {
    case 'thin':       return `.sheet{border:1.5px solid ${p.accent};}`;
    case 'double':     return `.sheet{border:4px double ${p.accent};}`;
    case 'ornamental': return `.sheet{border:2px solid ${p.accent};outline:5px solid ${p.accent};`
      + `outline-offset:4px;}`;
    case 'corners':    return `.sheet{background-image:`
      + `linear-gradient(${p.accent},${p.accent}),linear-gradient(${p.accent},${p.accent}),`
      + `linear-gradient(${p.accent},${p.accent}),linear-gradient(${p.accent},${p.accent});`
      + `background-size:34px 2px,2px 34px,34px 2px,2px 34px;background-repeat:no-repeat;`
      + `background-position:left top,left top,right bottom,right bottom;}`;
    default:           return '';
  }
};

/**
 * The stylesheet for one design in one parish's colours.
 *
 * Everything that is not the page box is expressed through the palette, so the
 * same design prints green for one parish and maroon for another without a
 * second template existing.
 */
export const layoutCss = (d: PrintDesign, brandColour?: string): string => {
  const p = buildPalette(brandColour || '');
  const sidebar = d.header === 'sidebar';

  return `
.sheet{position:relative;font-family:${d.bodyFont};font-size:${d.bodySize};
  line-height:${d.lineHeight};color:${p.ink};background:${p.paper};
  padding:${sidebar ? '0' : d.pagePadding};min-height:${A4.heightMm}mm;
  ${sidebar ? 'display:flex;align-items:stretch;' : ''}
  ${d.align === 'center' ? 'text-align:center;' : ''}}
.sheet-main{${sidebar ? 'flex:1;padding:20mm 16mm;' : ''}display:flex;flex-direction:column;}
.logo{display:block;max-width:100%;object-fit:contain;
  ${d.logo === 'center' ? 'margin:0 auto 10px;' : ''}}
.logo-fallback{display:block;font-weight:700;font-size:15px;letter-spacing:.06em;
  color:${p.accentDark};margin-bottom:6px;}
.identity{min-width:0;}
.church-name{margin:0;font-size:18px;font-weight:700;font-family:${d.headingFont};}
.church-addr{margin:3px 0 0;font-size:11px;opacity:.85;}
.church-tel{margin:2px 0 0;font-size:11px;opacity:.85;}
.masthead-left{display:flex;align-items:center;gap:14px;min-width:0;}
.body{flex:1;${sidebar ? '' : d.header === 'band' || d.header === 'diagonal'
    || d.header === 'wave' || d.header === 'split' ? 'padding:0 16mm;' : ''}}
${d.title === 'boxed' ? '' : `.doc-title{}`}
.lead{margin:0 0 6px;color:${p.inkSoft};}
.subject{margin:0 0 14px;font-size:24px;font-weight:700;font-family:${d.headingFont};
  color:${p.accentDark};}
.line{margin:5px 0;}
.ref{margin:18px 0 0;font-size:11px;color:${p.inkFaint};}
.details{width:100%;border-collapse:collapse;text-align:left;
  ${d.align === 'center' ? 'max-width:460px;margin:0 auto;' : ''}}
.details th{width:210px;text-align:left;font-weight:400;color:${p.inkSoft};
  padding:${d.cellPadding};vertical-align:top;}
.details td{padding:${d.cellPadding};font-weight:600;}
.details tr{border-bottom:1px solid ${p.ruleSoft};}
.rows{width:100%;border-collapse:collapse;text-align:left;}
.signatures{display:flex;justify-content:space-between;gap:28px;margin-top:46px;font-size:12px;}
.signatures i{display:block;border-top:1px solid ${p.inkSoft};width:170px;margin-bottom:5px;}
${headerCss(d, p)}
${titleCss(d, p)}
${tableCss(d, p)}
${footerCss(d, p)}
${borderCss(d, p)}
${decorationCss(d, p)}
${d.watermark ? `.sheet::after{content:"{{churchName}}";position:absolute;top:44%;left:0;right:0;
  text-align:center;font-size:66px;font-weight:700;color:${rgba(p.accent, 0.06)};
  letter-spacing:.1em;pointer-events:none;}` : ''}
`.trim();
};

/** Two ruled signature lines, which a register does not need but a certificate does. */
const SIGNATURES = `<div class="signatures">`
  + `<span><i></i>Minister</span><span><i></i>Parish Secretary</span></div>`;

/**
 * One design rendered for one document, as a print template row.
 *
 * The result is ordinary HTML in the three columns the register already has —
 * there is no new storage and nothing here is a second print engine.
 */
export const renderLayout = (
  designId: string,
  documentKey: string,
  brandColour?: string
): PrintTemplate => {
  const d = resolvePrintTemplate(designId);
  const doc = documentFor(documentKey);
  const label = documentLabel(documentKey);
  const sidebar = d.header === 'sidebar';
  const certificate = doc?.category === 'Certificates';

  const inner = `<div class="body">${documentBodyHtml(documentKey)}`
    + (certificate ? SIGNATURES : '')
    + `</div>`;

  const html = `<style>${layoutCss(d, brandColour)}</style>`
    + `<div class="sheet">`
    + (sidebar
      ? `${headerHtmlFor(d, label)}<div class="sheet-main">${titleBlock(label)}${inner}`
        + `${footerHtmlFor(d)}</div>`
      : `<div class="sheet-main">${headerHtmlFor(d, label)}${inner}${footerHtmlFor(d)}</div>`)
    + `</div>`;

  return {
    templateName: d.label,
    module: doc?.module || '',
    documentType: documentKey,
    htmlContent: html,
    headerHtml: '',
    footerHtml: '',
    pageSize: 'A4',
    orientation: doc?.landscape ? 'LANDSCAPE' : 'PORTRAIT',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    isActive: true,
    isDefault: false
  };
};

/** Every design, rendered for one document — what the picker shows. */
export const layoutsFor = (documentKey: string, brandColour?: string) =>
  PRINT_TEMPLATE_LIST.map((d) => ({
    design: d,
    template: renderLayout(d.id, documentKey, brandColour)
  }));

export { A4 };
